import { useState, useEffect, useMemo } from 'react';
import { useFlow } from '../../contexts/flow';
import { useAuth } from '@Contexts/auth';
import serviceMethods from '../../service';
import { useSearchParams } from 'react-router-dom';
import { DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import FlowBuilderSortableStage from '../FlowBuilderSortableStage';
import FlowBuilderLeftSidebar from '../FlowBuilderLeftSidebar';
import FlowBuilderStageSidebar from '../FlowBuilderStageSidebar';
import FlowBuilderTriggerSidebar from '../FlowBuilderTriggerSidebar';
import Modal from '../Modal';
import ModalLg from '../ModalLg';
import { useModal } from '../../contexts/modal';
import TriggerConfigModal from '../TriggerConfigModal';
import { orderFlowStages } from '../../utils';
import { getStageCustomFormIds } from '../../utils/flowStage';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Breadcrumbs from '@Components/Breadcrumbs';
import { toastSuccess, toastError } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import FlowDebugSlideout from '../FlowDebug/FlowDebugSlideout';
import { StageConditionsEditor } from '../FlowBuilderStageSidebar/StageConditionsEditor';
import { isUpcomingScheduleComplete } from '../FlowBuilderStageSidebar/UpcomingScheduleSection';
import './conditionsDrawer.css';

const APP_ENV = import.meta.env.VITE_APP_ENV;
const DEBUG_APP_ENVS = ['local', 'development', 'staging'];

export default function FlowBuilder() {
  const { user, hasPermission } = useAuth();
  // FLOW_BUILDER_READ grants a view-only builder; every control that creates,
  // updates, or deletes flows, stages, or triggers requires FLOW_BUILDER_WRITE.
  const canWrite = hasPermission(['FLOW_BUILDER_WRITE']);
  const [selectedStageIndex, setSelectedStageIndex] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [stageDepArray, setStageDepArray] = useState([]);
  const [updateDisabled, setUpdateDisabled] = useState(true);
  const [modalContent, setModalContent] = useState(null);
  const [activeTab, setActiveTab] = useState('stage');
  const [selectedTrigger, setSelectedTrigger] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [conditionsDrawerOpen, setConditionsDrawerOpen] = useState(false);
  const { openModal, closeModal } = useModal();
  const {
    getAllFlowStages,
    getFlowById,
    getAllFlows,
    getAllForms,
    getAllCustomForms,
  } = serviceMethods;
  const {
    flowDetails,
    setFlowDetails,
    allFlows,
    setAllFlows,
    allTypeformForms,
    setAllTypeformForms,
    allCustomForms,
    setAllCustomForms,
    currentFlowStages,
    setCurrentFlowStages,
    flowStageTypes,
    setFlowStages,
  } = useFlow();
  const typeDependencies = flowStageTypes?.filter(
    (type) =>
      type.id === currentFlowStages?.[selectedStageIndex]?.flowStageTypeId,
  )[0]?.dependencies;

  const handleSubmit = async (e) => {
    e.preventDefault();

    const stagesArray = [];
    for (const stage of currentFlowStages) {
      stagesArray.push(stage);
    }
    const payload = {
      ...flowDetails,
      flowStages: stagesArray,
      userId: user.id,
    };

    if (flowDetails.id) {
      const res = await serviceMethods.updateFlow(payload);
      if (res.ok) {
        // Update success
        toastSuccess('Flow updated');
        const data = await res.json();
        setAllFlows((prevFlows) =>
          prevFlows?.map((flow) => (flow.id !== data.id ? flow : data)),
        );
      } else {
        // Update error message
        toastError('Error updating Flow')
      }
    } else {
      const res = await serviceMethods.createFlow(payload);
      if (res.ok) {
        // Create success
        toastSuccess('Flow updated');
        const data = await res.json();
        setAllFlows((prevFlows) => [...prevFlows, data]);
        setFlowDetails(data);
        // Re-seed the builder with the persisted stages (DB-generated ids) so the
        // next save routes through updateFlow and matches stages by their real ids.
        setCurrentFlowStages(orderFlowStages(data.flowStages));
        setSearchParams({ id: data.id });
      } else {
        // Create error message
        toastError('Error creating Flow')
      }
    }
    getMatchingFlow(flowDetails.id);
  };

  const deleteFlow = async () => {
    const res = await serviceMethods.deleteFlow(flowDetails.id, user.id);
    if (res.ok) {
      toastSuccess('Flow deleted');
      const data = await res.json();
      setAllFlows((prevFlows) =>
        prevFlows.filter((flow) => flow.id !== data.id),
      );
      setFlowDetails({
        id: null,
        flowTitle: null,
      });
      setCurrentFlowStages([]);
      setSearchParams({});
    } else {
      toastError('Error deleting Flow')
    }
    setSelectedStageIndex(null);
    closeModal();
  };

  const deleteFlowStage = async (flowStageId, i) => {
    const updatedCurrentFlowStages = currentFlowStages.filter(
      (_, index) => index !== i,
    );

    const reorderedFlowStages = updatedCurrentFlowStages?.map(
      (flowStage, idx) => {
        const currentStageNewOrder = flowStage.id
          ? updatedCurrentFlowStages.findIndex(
              (stage) => stage.id === flowStage.id,
            ) + 1
          : idx + 1;

        return {
          ...flowStage,
        };
      },
    );
    setCurrentFlowStages(reorderedFlowStages);

    if (!flowStageId || updateDisabled) {
      toastSuccess('Flow Stage Deleted')
      setSelectedStageIndex(null);
      closeModal();
      return;
    }

    const payload = {
      ...flowDetails,
      flowStages: reorderedFlowStages,
      userId: user.id,
    };
    if (flowDetails.id) {
      const res = await serviceMethods.updateFlow(payload);
      if (res.ok) {
        toastSuccess('Flow Stage Deleted')
        const data = await res.json();
      } else {
        toastError('Error deleting Flow Stage')
      }
    } else {
      const res = await serviceMethods.createFlow(payload);
      if (res.ok) {
        toastSuccess('Flow Stage Deleted')
        const data = await res.json();
        setFlowDetails({
          ...flowDetails,
          id: data.id,
        });
        setSearchParams({ id: data.id });
      } else {
        toastError('Error deleting Flow Stage')
      }
    }
    getMatchingFlow(flowDetails.id);
    setSelectedStageIndex(null);
    closeModal();
  };

  const handleOpenModal = (modalContentString) => {
    setModalContent(modalContentString);
    openModal();
  };

  const handleDragEnd = (event) => {
    if (!canWrite) return;

    const { over, active } = event;

    // Reorder within flow
    if (active.id !== over?.id) {
      setCurrentFlowStages((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over?.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const fetchStagesAndFlows = async () => {
    try {
      const flowStages = await getAllFlowStages();
      setFlowStages(flowStages);

      const flows = await getAllFlows();
      setAllFlows(flows);

      const typeformForms = await getAllForms();
      const typeformFormsData = await typeformForms.json();
      setAllTypeformForms(typeformFormsData);

      const customForms = await getAllCustomForms();
      const customFormsData = await customForms.json();
      setAllCustomForms(customFormsData);
    } catch {
      console.error('Unable to fetch stages and flows');
    }
  };

  const getMatchingFlow = async (id = flowDetails.id) => {
    try {
      if (id) {
        const matchingFlow = await getFlowById(id);
        if (!matchingFlow.ok) {
          setSearchParams({});
          return;
        } else {
          const data = await matchingFlow.json();
          const reorderedFlowStages = orderFlowStages(data.flowStages);
          setFlowDetails(data);
          setCurrentFlowStages(reorderedFlowStages);
        }
      }
    } catch {
      console.error('Unable to get matching flow');
    }
  };

  const buildStageDeps = () => {
    let deps = [];
    if (flowDetails?.flowStages?.length) {
      const orderedStages = orderFlowStages(flowDetails.flowStages);
      deps = orderedStages?.map((stage, i) => {
        const stageDepObj = {};
        let loop = 0;
        const tempType = flowStageTypes.find(
          (type) => type.id === stage.flowStageTypeId,
        );
        if (tempType) {
          for (const typeDep of tempType.dependencies) {
            stageDepObj[typeDep] = stage.dependencyIdentifiers[loop];
            loop++;
          }
        }
        return stageDepObj;
      });
    }
    setStageDepArray(deps);
  };

  useEffect(() => {
    fetchStagesAndFlows();

    const flowId = searchParams.get('id');
    if (flowId) {
      getMatchingFlow(flowId);
    } else if (flowDetails.id) {
      getMatchingFlow();
    }
  }, []);

  useEffect(() => {
    setSelectedStageIndex(null);
  }, [flowDetails.title, flowDetails.id]);

  useEffect(() => {
    buildStageDeps();
  }, [selectedStageIndex]);

  useEffect(() => {
    if (!flowDetails?.categoryId) {
      setUpdateDisabled(true);
      return;
    }
    if (currentFlowStages?.length && flowDetails.flowTitle) {
      const checkForMissingProperties = currentFlowStages.filter((stage) => {
        const flowStageType = flowStageTypes.find(
          (type) => type.id === stage.flowStageTypeId,
        );

        if (
          !stage.flowStageTypeId ||
          !stage.title ||
          (flowStageType?.dependencies &&
            flowStageType.dependencies.length > 0 &&
            stage.dependencyIdentifiers.length < 1) ||
          (flowStageType?.upcoming &&
            !isUpcomingScheduleComplete(flowStageType.upcoming, stage.conditions?.schedule))
        ) {
          return true;
        }

        return false;
      });

      let hasEmptyDeps = false;

      for (const currentStage of currentFlowStages) {
        const flowStageType = flowStageTypes.find(
          (type) => type.id === currentStage.flowStageTypeId,
        );

        // Skip stages that don't have any dependencies
        if (
          flowStageType?.dependencies &&
          flowStageType.dependencies.length === 0
        ) {
          continue;
        }

        for (let i = 0; i < currentStage?.dependencyIdentifiers?.length; i++) {
          if (Array.isArray(currentStage.dependencyIdentifiers)) {
            if (
              currentStage.dependencyIdentifiers[i] === undefined ||
              currentStage.dependencyIdentifiers[i].length === 0 ||
              currentStage.dependencyIdentifiers[i] === 0 ||
              currentStage.dependencyIdentifiers[i] === ''
            ) {
              hasEmptyDeps = true;
              break;
            }

            for (const dep of currentStage.dependencyIdentifiers[i]) {
              if (dep === undefined || dep.length < 1 || dep === '') {
                hasEmptyDeps = true;
                break;
              }
            }

            const checkForEmptyDeps = currentStage.dependencyIdentifiers.filter(
              (dep) => dep === undefined || dep.length < 1 || dep === '',
            );

            if (checkForEmptyDeps.length > 0) {
              hasEmptyDeps = true;
              break;
            }
          }
        }
      }
      if (checkForMissingProperties.length || hasEmptyDeps) {
        setUpdateDisabled(true);
      } else {
        setUpdateDisabled(false);
      }
    } else {
      setUpdateDisabled(true);
    }
  }, [currentFlowStages, flowDetails.flowTitle, flowDetails.categoryId]);

  const handleStageConditionsChange = (conditions) => {
    setCurrentFlowStages((prevStages) =>
      prevStages.map((stage, index) =>
        index === selectedStageIndex ? { ...stage, conditions } : stage,
      ),
    );
  };

  const selectedStage =
    selectedStageIndex !== null ? currentFlowStages?.[selectedStageIndex] : null;

  const selectedStageCustomFormIds = useMemo(
    () =>
      getStageCustomFormIds(
        flowStageTypes?.find(
          (type) => type.id === selectedStage?.flowStageTypeId,
        )?.dependencies,
        selectedStage?.dependencyIdentifiers,
      ),
    [flowStageTypes, selectedStage?.flowStageTypeId, selectedStage?.dependencyIdentifiers],
  );

  return (
    <div className="font-questrial flex h-full w-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs
          navList={[
            { name: 'Flows', href: '/flows' },
            { name: 'Builder', href: '/flows/builder' },
          ]}
        />
        {DEBUG_APP_ENVS.includes(APP_ENV) && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setDebugOpen(true)}
          >
            Debug
          </Button>
        )}
      </div>

      <div className="flex h-full rounded bg-gray-100 shadow-md">
        {/* Flow Canvas */}

        <DndContext
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          {/* Sidebar Left */}
          <FlowBuilderLeftSidebar
            currentFlowStages={currentFlowStages}
            setCurrentFlowStages={setCurrentFlowStages}
            flowDetails={flowDetails}
            setFlowDetails={setFlowDetails}
            setSearchParams={setSearchParams}
            allFlows={allFlows}
            handleSubmit={handleSubmit}
            updateDisabled={updateDisabled}
            handleOpenModal={handleOpenModal}
            setAllTypeformForms={setAllTypeformForms}
            getMatchingFlow={getMatchingFlow}
            canWrite={canWrite}
          />

          {/* Flow / Center */}
          <div
            className="flex w-full flex-col items-center overflow-y-auto p-6"
            onClick={() => {
              setSelectedStageIndex(null);
            }}
          >
            {currentFlowStages?.length === 0 ? (
              <div>
                <span className="text-gray-400">
                  Add a Stage to build your flow
                </span>
              </div>
            ) : (
              <SortableContext
                items={currentFlowStages?.map((stage) => stage.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="flex w-full flex-col items-center gap-4">
                  {currentFlowStages?.map((stage, i) => (
                    <FlowBuilderSortableStage
                      key={stage.id}
                      stage={stage}
                      id={stage.id}
                      i={i}
                      currentFlowStages={currentFlowStages}
                      selectedStageIndex={selectedStageIndex}
                      setSelectedStageIndex={setSelectedStageIndex}
                      stageDepArray={stageDepArray}
                      flowStageTypes={flowStageTypes}
                      allTypeformForms={allTypeformForms}
                      allCustomForms={allCustomForms}
                    />
                  ))}
                </div>
              </SortableContext>
            )}
          </div>

          {/* Sidebar Right */}

          <div className="flex h-full w-[40%] flex-col rounded-r border-l bg-white shadow-lg">
            {/* Tab Buttons */}
            <div className="flex">
              <button
                onClick={() => setActiveTab('stage')}
                className={`flex-1 py-2 text-sm font-medium ${
                  activeTab === 'stage'
                    ? 'font-bold text-black'
                    : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                Stage
              </button>
              <button
                onClick={() => setActiveTab('triggers')}
                className={`flex-1 py-2 text-sm font-medium ${
                  activeTab === 'triggers'
                    ? 'font-bold text-black'
                    : 'bg-gray-100 text-gray-500 hover:text-gray-700'
                }`}
              >
                Triggers
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex h-full flex-col overflow-y-auto">
              {activeTab === 'stage' && (
                <FlowBuilderStageSidebar
                  selectedStageIndex={selectedStageIndex}
                  currentFlowStages={currentFlowStages}
                  setCurrentFlowStages={setCurrentFlowStages}
                  stageDepArray={stageDepArray}
                  setStageDepArray={setStageDepArray}
                  flowStageTypes={flowStageTypes}
                  typeDependencies={typeDependencies}
                  allTypeformForms={allTypeformForms}
                  allCustomForms={allCustomForms}
                  handleOpenModal={handleOpenModal}
                  canWrite={canWrite}
                  onEditConditions={() => setConditionsDrawerOpen(true)}
                />
              )}
              {activeTab === 'triggers' && (
                <FlowBuilderTriggerSidebar
                  selectedStageIndex={selectedStageIndex}
                  currentFlowStages={currentFlowStages}
                  handleOpenModal={handleOpenModal}
                  setSelectedTrigger={setSelectedTrigger}
                  updateDisabled={updateDisabled}
                  canWrite={canWrite}
                />
              )}
            </div>
          </div>
        </DndContext>

        {conditionsDrawerOpen && (
          <div
            className="conditions-drawer-backdrop fixed inset-0 z-50 flex flex-col justify-end bg-gray-900/50"
            onClick={() => setConditionsDrawerOpen(false)}
          >
            <div
              className="conditions-drawer flex flex-col overflow-hidden rounded-t-xl border-t border-zinc-200 bg-white shadow-2xl"
              style={{ height: '65vh' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex flex-none items-center justify-between border-b border-zinc-200 px-6 py-3">
                <div className="text-base font-medium">Conditions</div>
                <button
                  type="button"
                  onClick={() => setConditionsDrawerOpen(false)}
                  className="rounded-md bg-zinc-900 px-4 py-1.5 text-sm text-white hover:bg-zinc-800"
                >
                  Done
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {selectedStage && (
                  <StageConditionsEditor
                    key={`drawer-conditions-${selectedStage.id}`}
                    variant="grouped"
                    summary={selectedStage.conditionsSummary}
                    flowStageType={flowStageTypes?.find(
                      (type) => type.id === selectedStage.flowStageTypeId,
                    )}
                    initialConditions={selectedStage.conditions}
                    onChange={handleStageConditionsChange}
                    canWrite={canWrite}
                    customFormIds={selectedStageCustomFormIds}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {modalContent === 'delete-flow' && (
          <Modal className={'max-w-xl'}>
            <div className="flex flex-col items-center p-4">
              <h2 className="text-lg font-bold">Delete Flow.</h2>
              <h3 className="mb-4 text-lg">Are you sure?</h3>
              <div className="flex gap-4">
                <Button
                  variant='destructive'
                  className=""
                  onClick={() => deleteFlow()}
                  disabled={!canWrite}
                >
                  Delete
                </Button>

                <Button
                  variant='outline'
                  className=""
                  onClick={() => closeModal()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal>
        )}
        {modalContent === 'delete-stage' && (
          <Modal className={'max-w-xl'}>
            <div className="flex flex-col items-center p-4">
              <h2 className="text-lg font-bold">Delete Stage.</h2>
              <h3 className="mb-4 text-lg">Are you sure?</h3>
              <div className="flex gap-4">
                <Button
                  variant='destructive'
                  className=""
                  onClick={() =>
                    deleteFlowStage(
                      currentFlowStages[selectedStageIndex]?.id,
                      selectedStageIndex,
                    )
                  }
                  disabled={!canWrite}
                >
                  Delete
                </Button>

                <Button
                  variant='outline'
                  className=""
                  onClick={() => closeModal()}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Modal>
        )}
        {modalContent === 'trigger' && (
          <ModalLg>
            <TriggerConfigModal
              selectedStageIndex={selectedStageIndex}
              selectedTrigger={selectedTrigger}
              setSelectedTrigger={setSelectedTrigger}
              closeModal={closeModal}
              setSearchParams={setSearchParams}
            />
          </ModalLg>
        )}

        {DEBUG_APP_ENVS.includes(APP_ENV) && (
          <FlowDebugSlideout
            open={debugOpen}
            onOpenChange={setDebugOpen}
            flowId={flowDetails.id}
            flowStages={currentFlowStages ?? []}
            flowStageTypes={flowStageTypes ?? []}
          />
        )}
      </div>
    </div>
  );
}
